import {Button, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import {useFormik} from 'formik'
import {ChangeEvent, useEffect, useState} from 'react'
import {useQuery} from '@apollo/client'
import {GET_DOCUMENTS_POLICY} from '../../../gql/queries/policiesApiQueries'
import {IResAllTagsDocuments} from '../../../types/policiesApi'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {UPDATE_RESOURCE_CENTER_STR} from '../../../gql/mutations/resourceCenterMutations'
import {parseId} from '../../../helpers'
import {normalizeResourceType} from '../resourceTypeHelpers'
import {IResourceCenter, IUpdateResourceCenterResponse} from '../../../types/resourceCenter'
import {IResourceCategoryTreeResponse} from '../../../types/resourceCategory'
import {GET_RESOURCE_CATEGORY_TREE} from '../../../gql/queries/resourceCategoryQuery'
import {flattenCategoryTree} from '../categoryTreeHelpers'
import {useDisclourse} from '../../../hooks/useDisclourse'
import {ModalManageTags} from './ModalManageTags'
import {prependBackendUrl} from '../../../gql/client'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import {
  BACKEND_ERROR,
  KB_UNIT,
  REQUIRED_MARK,
  RESOURCE_MESSAGES as R_,
} from '../resourceCenterMessages'
import { resourceTypeOptions } from '../resourceTypeHelpers'

/** Fabrica: los mensajes necesitan `intl`. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
  name: Yup.string().required(intl.formatMessage(R_.vNameRequired)),
  resourceType: Yup.string().required(intl.formatMessage(R_.vTypeRequired)),
  url: Yup.string().when('resourceMode', {
    is: 'url',
    then: (schema) => schema.required(intl.formatMessage(R_.vUrlRequired)).url(intl.formatMessage(R_.vUrlInvalid)),
    otherwise: (schema) => schema.notRequired(),
  }),
})

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  resource: IResourceCenter
}

export const ModalEditResource: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  resource,
}) => {
  const intl = useIntl()
  const [uploadFile, {data, error, loading}] = useUploadFile<IUpdateResourceCenterResponse>()
  const [newFile, setNewFile] = useState<File | null>(null)
  const [newThumbnail, setNewThumbnail] = useState<File | null>(null)
  const {onOpen: onOpenManageTags, isOpen: isOpenManageTags, onClose: onCloseManageTags} = useDisclourse()
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)

  const {
    data: dataTags,
    loading: loadingTags,
    error: errorTags,
  } = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'cache-first',
  })

  // El modal consulta el árbol directamente (cache-and-network) para que las
  // carpetas recién creadas aparezcan siempre, sin depender del estado de la página
  const {data: dataCategories, loading: loadingCategories} =
    useQuery<IResourceCategoryTreeResponse>(GET_RESOURCE_CATEGORY_TREE, {
      fetchPolicy: 'cache-and-network',
    })

  const formik = useFormik({
    initialValues: {
      name: resource.name,
      description: resource.description || '',
      url: resource.url || '',
      resourceMode: (resource.url ? 'url' : 'file') as 'file' | 'url',
      resourceType: normalizeResourceType(resource.resourceType),
      documentTagId: resource.documentTag?.id || '',
      categoryId: resource.category?.id || '',
      comments: resource.comments || '',
      isActive: resource.isActive,
      order: resource.order,
    },
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (v) => {
      const formdata = new FormData()

      // Mapa multipart dinámico: archivo nuevo y/o miniatura nueva
      const map: Record<string, string[]> = {}
      let partIndex = 0
      if (v.resourceMode === 'file' && newFile) {
        formdata.append(String(partIndex), newFile, newFile.name)
        map[String(partIndex)] = ['variables.resourceData.file']
        partIndex++
      }
      if (newThumbnail) {
        formdata.append(String(partIndex), newThumbnail, newThumbnail.name)
        map[String(partIndex)] = ['variables.resourceData.thumbnail']
        partIndex++
      }

      const obj = {
        query: UPDATE_RESOURCE_CENTER_STR,
        variables: {
          id: parseId(resource.id),
          resourceData: {
            name: v.name,
            description: v.description || undefined,
            ...(v.resourceMode === 'file' && newFile ? {file: null} : {}),
            ...(newThumbnail ? {thumbnail: null} : {}),
            ...(v.resourceMode === 'url' && v.url ? {url: v.url} : {}),
            resourceType: v.resourceType,
            documentTagId: v.documentTagId ? parseId(v.documentTagId) : null,
            categoryId: v.categoryId ? parseId(v.categoryId) : null,
            comments: v.comments || undefined,
            isActive: v.isActive,
            order: v.order,
          },
        },
      }

      formdata.append('map', JSON.stringify(map))
      formdata.append('operations', JSON.stringify(obj))

      try {
        await uploadFile(formdata)
      } catch (err) {
        console.error('Error updating resource:', err)
        toast.error(intl.formatMessage(R_.errUpdateResource))
      }
    },
  })

  // Generate preview for images
  useEffect(() => {
    if (newFile && newFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(newFile)
      setPreview(url)
      return () => URL.revokeObjectURL(url)
    } else if (!newFile && resource.file) {
      // Show current file preview if it's an image
      const ext = resource.file.split('.').pop()?.toLowerCase()
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')) {
        setPreview(prependBackendUrl(resource.file))
      }
    } else {
      setPreview(null)
    }
  }, [newFile, resource.file])

  // Handle paste
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!isOpen) return
      if (!e.clipboardData) return

      const files = Array.from(e.clipboardData.files || [])
      if (files.length > 0 && files[0] instanceof File) {
        e.preventDefault()
        setIsDragging(true)
        setNewFile(files[0])
        toast.info(intl.formatMessage(R_.toastFilePasted))
        setTimeout(() => setIsDragging(false), 1000)
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(BACKEND_ERROR, {message: error.toString()}))
  }, [error])

  useEffect(() => {
    if (errorTags) toast.error(intl.formatMessage(BACKEND_ERROR, {message: errorTags.message}))
  }, [errorTags])

  useEffect(() => {
    if (!data) return
    if (data.updateResourceCenter.success) {
      onClose()
      refetch()
      toast.success(intl.formatMessage(R_.toastResourceUpdated))
    } else if (
      data.updateResourceCenter.errors &&
      data.updateResourceCenter.errors.length > 0
    ) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: data.updateResourceCenter.errors[0].message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    setNewFile(null)
    setNewThumbnail(null)
    setPreview(null)
    onClose()
  }

  const currentFileName = resource.file?.split('/').pop()?.split('?')[0] || resource.url || 'No file/URL'

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-pencil-square" /> {intl.formatMessage(R_.editResource)}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Current Resource Info */}
            <Col md={12}>
              <div className="alert alert-info mb-3">
                <strong>{intl.formatMessage(R_.currentResource)}</strong> {currentFileName}
                <br />
                <small className="text-muted">
                  {intl.formatMessage(resource.url ? R_.isUrlResource : R_.isFileResource)}
                  {intl.formatMessage(R_.switchHint)}
                </small>
              </div>
            </Col>

            {/* Resource Mode Selector */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.resourceType)}</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="mode-file-edit"
                    name="resourceMode"
                    label={intl.formatMessage(R_.uploadFile)}
                    checked={formik.values.resourceMode === 'file'}
                    onChange={() => {
                      formik.setFieldValue('resourceMode', 'file')
                      formik.setFieldValue('url', '')
                    }}
                  />
                  <Form.Check
                    type="radio"
                    id="mode-url-edit"
                    name="resourceMode"
                    label={intl.formatMessage(R_.externalUrl)}
                    checked={formik.values.resourceMode === 'url'}
                    onChange={() => {
                      formik.setFieldValue('resourceMode', 'url')
                      setNewFile(null)
                      setPreview(null)
                    }}
                  />
                </div>
              </Form.Group>
            </Col>

            {/* File Upload (Optional) */}
            {formik.values.resourceMode === 'file' && (
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{intl.formatMessage(R_.replaceFile)}</Form.Label>
                <div
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault()
                    setIsDragging(false)
                    const files = Array.from(e.dataTransfer.files || [])
                    if (files.length > 0 && files[0] instanceof File) {
                      setNewFile(files[0])
                    }
                  }}
                  className={`border rounded p-3 text-center ${
                    isDragging ? 'bg-light border-primary' : 'border-secondary'
                  }`}
                  style={{cursor: 'pointer', borderStyle: 'dashed'}}
                >
                  {isDragging ? (
                    <span className="text-primary fw-bold">{intl.formatMessage(R_.dropFileHere)}</span>
                  ) : (
                    <span className="text-muted">{intl.formatMessage(R_.dragOrClickNew)}</span>
                  )}
                </div>

                <Form.Control
                  name="newFile"
                  onChange={(e) => {
                    const file = (e as ChangeEvent<HTMLInputElement>)?.target?.files?.[0]
                    if (file) {
                      setNewFile(file)
                    }
                  }}
                  type="file"
                  className="mt-2"
                />

                {(newFile || preview) && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <div className="d-flex align-items-center gap-3">
                      {preview && (
                        <img
                          src={preview}
                          alt={intl.formatMessage(R_.previewAlt)}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      )}
                      <div className="flex-fill">
                        {newFile ? (
                          <>
                            <div className="fw-bold">{newFile.name}</div>
                            <small className="text-muted">
                              {(newFile.size / 1024).toFixed(2)}
{KB_UNIT}
                            </small>
                            <div className="badge bg-success ms-2">{intl.formatMessage(R_.newFile)}</div>
                          </>
                        ) : (
                          <>
                            <div className="fw-bold">{currentFileName}</div>
                            <div className="badge bg-info">{intl.formatMessage(R_.currentFile)}</div>
                          </>
                        )}
                      </div>
                      {newFile && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setNewFile(null)
                            setPreview(null)
                          }}
                        >
                          <i className="bi bi-x-lg" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
            )}

            {/* URL Input */}
            {formik.values.resourceMode === 'url' && (
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    {intl.formatMessage(R_.url)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                  </Form.Label>
                  <Form.Control
                    name="url"
                    type="url"
                    placeholder={intl.formatMessage(R_.urlPlaceholder)}
                    value={formik.values.url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={Boolean(formik.errors.url && formik.touched.url)}
                  />
                  {formik.touched.url && formik.errors.url && (
                    <Form.Text className="text-danger">{formik.errors.url}</Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    {intl.formatMessage(R_.urlSupported)}
                  </Form.Text>
                </Form.Group>
              </Col>
            )}

            {/* Name */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {intl.formatMessage(R_.name)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                </Form.Label>
                <Form.Control
                  isInvalid={Boolean(formik.errors.name && formik.touched.name)}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  name="name"
                  placeholder={intl.formatMessage(R_.namePlaceholder)}
                />
                {formik.errors.name && formik.touched.name && (
                  <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Resource Type */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {intl.formatMessage(R_.resourceType)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                </Form.Label>
                <Select
                  name="resourceType"
                  onBlur={formik.handleBlur}
                  value={resourceTypeOptions(intl).find(
                    (opt) => opt.value === formik.values.resourceType
                  )}
                  onChange={(option) => {
                    formik.setFieldValue('resourceType', option?.value || 'document')
                  }}
                  options={resourceTypeOptions(intl)}
                />
                {formik.errors.resourceType && formik.touched.resourceType && (
                  <Form.Text className="text-danger">{formik.errors.resourceType}</Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Description */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.descriptionField)}</Form.Label>
                <Form.Control
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  as="textarea"
                  rows={2}
                  placeholder={intl.formatMessage(R_.descriptionPlaceholder)}
                />
              </Form.Group>
            </Col>

            {/* Thumbnail (opcional) */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.thumbnail)}</Form.Label>
                <div className="d-flex align-items-center gap-3">
                  {(newThumbnail || resource.thumbnail) && (
                    <div style={{position: 'relative'}}>
                      <img
                        src={
                          newThumbnail
                            ? URL.createObjectURL(newThumbnail)
                            : prependBackendUrl(resource.thumbnail || '')
                        }
                        alt={intl.formatMessage(R_.thumbnailAlt)}
                        style={{width: 90, height: 60, objectFit: 'cover', borderRadius: 4}}
                      />
                      {newThumbnail ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="position-absolute top-0 end-0 p-0 px-1"
                          onClick={() => setNewThumbnail(null)}
                        >
                          <i className="bi bi-x" />
                        </Button>
                      ) : (
                        <span className="badge bg-info position-absolute bottom-0 start-0">
                        {intl.formatMessage(R_.currentThumbnail)}
                        </span>
                      )}
                    </div>
                  )}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = (e as ChangeEvent<HTMLInputElement>)?.target?.files?.[0]
                      if (file) setNewThumbnail(file)
                    }}
                  />
                </div>
                <Form.Text className="text-muted">
                  {intl.formatMessage(R_.thumbnailHint)}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Tag */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <Form.Label className="fw-bold">{intl.formatMessage(R_.tag)}</Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mb-2 text-decoration-none"
                    onClick={onOpenManageTags}
                    title={intl.formatMessage(R_.createNewTag)}
                  >
                    <i className="bi bi-plus-circle me-1" />
                    {intl.formatMessage(R_.newTag)}
                  </Button>
                </div>
                <Select
                  isClearable
                  isLoading={loadingTags}
                  value={
                    formik.values.documentTagId
                      ? dataTags?.allAttachmentsTags.edges
                          .map(({node}) => ({
                            label: node.tagName,
                            value: node.id,
                          }))
                          .find((opt) => opt.value === formik.values.documentTagId)
                      : null
                  }
                  onChange={(option) => {
                    formik.setFieldValue('documentTagId', option?.value || '')
                  }}
                  options={
                    dataTags?.allAttachmentsTags.edges.map(({node}) => ({
                      label: node.tagName,
                      value: node.id,
                    })) || []
                  }
                  placeholder={intl.formatMessage(R_.selectTag)}
                />
              </Form.Group>
            </Col>

            {/* Folder */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.folder)}</Form.Label>
                <Select
                  isClearable
                  isLoading={loadingCategories}
                  value={
                    flattenCategoryTree(dataCategories?.resourceCategoryTree || []).find(
                      (opt) => opt.value === formik.values.categoryId
                    ) || null
                  }
                  onChange={(option) => {
                    formik.setFieldValue('categoryId', option?.value || '')
                  }}
                  options={flattenCategoryTree(dataCategories?.resourceCategoryTree || [])}
                  placeholder={intl.formatMessage(R_.noFolderUncategorized)}
                />
              </Form.Group>
            </Col>

            {/* Order */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.displayOrder)}</Form.Label>
                <Form.Control
                  type="number"
                  name="order"
                  value={formik.values.order}
                  onChange={formik.handleChange}
                  placeholder="0"
                  min="0"
                />
                <Form.Text className="text-muted">
                {intl.formatMessage(R_.orderHintPriority)}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Comments */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.comments)}</Form.Label>
                <Form.Control
                  name="comments"
                  onChange={formik.handleChange}
                  value={formik.values.comments}
                  as="textarea"
                  rows={3}
                  placeholder={intl.formatMessage(R_.commentsPlaceholder)}
                />
              </Form.Group>
            </Col>

            {/* Is Active */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={intl.formatMessage(R_.activeVisible)}
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                />
              </Form.Group>
            </Col>

            {/* Stats */}
            <Col md={12}>
              <div className="alert alert-secondary">
                <div className="row text-center">
                  <div className="col">
                    <strong>{resource.viewsCount}</strong>
                    <div className="small text-muted">{intl.formatMessage(R_.totalViews)}</div>
                  </div>
                  <div className="col">
                    <strong>
{resource.created
? new Date(resource.created).toLocaleDateString()
: intl.formatMessage(R_.notAvailable)}
</strong>
                    <div className="small text-muted">{intl.formatMessage(R_.created)}</div>
                  </div>
                  <div className="col">
                    <strong>
{resource.updated
? new Date(resource.updated).toLocaleDateString()
: intl.formatMessage(R_.notAvailable)}
</strong>
                    <div className="small text-muted">{intl.formatMessage(R_.lastUpdated)}</div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
        {intl.formatMessage(R_.cancel)}
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={loading || loadingTags}
          onClick={() => formik.handleSubmit()}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(R_.updating)}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" />
              {intl.formatMessage(R_.updateResource)}
            </>
          )}
        </Button>
      </Modal.Footer>

      {isOpenManageTags && (
        <ModalManageTags
          isOpen={isOpenManageTags}
          onClose={onCloseManageTags}
          onTagCreated={(tag) => formik.setFieldValue('documentTagId', tag.id)}
        />
      )}
    </Modal>
  )
}
